import * as pdfjs from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ALPHA_THRESHOLD = 20;
const COLOR_THRESHOLD = 245;
const PDF_RENDER_SCALE = 2;

const base64ToBytes = (base64: string): Uint8Array =>
    Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

const isContentPixel = (red: number, green: number, blue: number, alpha: number): boolean =>
    alpha > ALPHA_THRESHOLD &&
    (red < COLOR_THRESHOLD || green < COLOR_THRESHOLD || blue < COLOR_THRESHOLD);

type ContentBounds = { top: number; left: number; width: number; height: number };

const findContentBounds = (
    data: Uint8ClampedArray,
    width: number,
    height: number
): ContentBounds | null => {
    let top = height;
    let left = width;
    let right = 0;
    let bottom = 0;
    let foundContent = false;

    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const index = (y * width + x) * 4;
            if (
                !isContentPixel(
                    data[index],
                    data[index + 1],
                    data[index + 2],
                    data[index + 3]
                )
            ) {
                continue;
            }

            foundContent = true;
            top = Math.min(top, y);
            bottom = Math.max(bottom, y);
            left = Math.min(left, x);
            right = Math.max(right, x);
        }
    }

    if (!foundContent) {
        return null;
    }

    return {
        top,
        left,
        width: right - left + 1,
        height: bottom - top + 1,
    };
};

const trimCanvasWhitespace = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    const context = canvas.getContext('2d');
    if (!context) {
        return canvas;
    }

    const { width, height } = canvas;
    const bounds = findContentBounds(context.getImageData(0, 0, width, height).data, width, height);
    if (!bounds) {
        return canvas;
    }

    const trimmed = document.createElement('canvas');
    trimmed.width = bounds.width;
    trimmed.height = bounds.height;

    const trimmedContext = trimmed.getContext('2d');
    if (!trimmedContext) {
        return canvas;
    }

    trimmedContext.drawImage(
        canvas,
        bounds.left,
        bounds.top,
        bounds.width,
        bounds.height,
        0,
        0,
        bounds.width,
        bounds.height
    );

    return trimmed;
};

export const renderPdfToImage = async (base64: string): Promise<string> => {
    const pdf = await pdfjs.getDocument({ data: base64ToBytes(base64) }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Canvas is not supported');
    }

    await page.render({ canvasContext: context, viewport }).promise;
    return trimCanvasWhitespace(canvas).toDataURL('image/png');
};
